import React from 'react';
import { activityThemes } from '../../../Components/LessonActivityCard';
import LessonShell from '../../../Components/LessonShell';

/**
 * Topic lesson adapter.
 *
 * A topic page hands over one child per activity section (each wrapping a
 * TopicsRouter with its own `sectionData`). Those children stay untouched --
 * TopicsRouter remains the thing that decides which activity component runs.
 * This adapter only converts them into panels for the shared LessonShell.
 */

// Pull the section's own data object off a child element so the step strip and
// panel headings can be labelled from the lesson data rather than hardcoded.
// Handles both shapes the pages use: a bare <TopicsRouter sectionData={...} />
// and a <div> wrapping one. Returns null if neither matches, and callers fall
// back to a generic label.
const readSectionData = (child) => {
  if (!React.isValidElement(child)) return null;
  if (child.props?.sectionData) return child.props.sectionData;

  const inner = child.props?.children;
  if (React.isValidElement(inner) && inner.props?.sectionData) {
    return inner.props.sectionData;
  }
  if (Array.isArray(inner)) {
    const match = inner.find(
      (node) => React.isValidElement(node) && node.props?.sectionData
    );
    return match ? match.props.sectionData : null;
  }
  return null;
};

// Title fields the JSON sections use, in priority order. Keeps the panel
// heading and "up next" text tied to the real lesson content.
const SECTION_TITLE_KEYS = [
  'languageExplanationTitle',
  'grammarQuizTitle',
  'readingcompTitle',
  'quizTitle',
  'vocabTitle',
  'discussionTitle',
  'videoTitle',
  'answersTitle',
  'title',
];

const readSectionTitle = (data) => {
  if (!data) return '';
  const key = SECTION_TITLE_KEYS.find((k) => typeof data[k] === 'string' && data[k].trim());
  return key ? data[key].trim() : '';
};

const TopicsPageLayout = ({
  title,
  image,
  level,
  children,
  seoTitle,
  seoDescription,
  description,
}) => {
  const panels = React.Children.toArray(children).map((child) => {
    const data = readSectionData(child);

    // Labels come from the shared activityThemes map, so a section always reads
    // the same way here and in its own card. No per-lesson naming to maintain.
    const label = activityThemes[data?.type]?.label || 'Activity';

    return {
      label,
      title: readSectionTitle(data) || label,
      content: child,
    };
  });

  return (
    <LessonShell
      eyebrow="Lesson"
      title={title}
      image={image}
      level={level}
      description={description}
      seoTitle={seoTitle}
      seoDescription={seoDescription}
      panels={panels}
    />
  );
};

export default TopicsPageLayout;